import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Button } from '@/ui/components/atoms/Button'
import { Loading } from '@/ui/components/atoms/Loading'
import { InformationSection } from '../InformationSection'
import { AvailabilitySection } from '../AvailabilitySection'
import { ExpertiseSection } from '../ExpertiseSection'
import { ZonesSection } from '../ZonesSection'
import { GeneralSection } from '../GeneralSection'
import { UnsavedChangesModal } from '../UnsavedChangesModal'
import { usePreferences } from '../../hooks/usePreferences'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { Preferences } from '../../types/preferences.types'
import './PreferencesView.styles.css'

export const PreferencesView: React.FC = () => {
  const [activeSection, setActiveSection] = useState('informations')
  const { 
    preferences, 
    isLoading, 
    error, 
    savePreferences, 
    isSaving 
  } = usePreferences()
  
  const [localPreferences, setLocalPreferences] = useState<Preferences | null>(null)
  const originalPreferencesRef = useRef<Preferences | null>(null)
  
  const hasChanges = useCallback(() => {
    if (!localPreferences || !originalPreferencesRef.current) return false
    return JSON.stringify(localPreferences) !== JSON.stringify(originalPreferencesRef.current)
  }, [localPreferences])
  
  const { showModal, setShowModal, reset } = useUnsavedChanges(hasChanges())

  useEffect(() => {
    if (preferences && !localPreferences) {
      setLocalPreferences(preferences)
      originalPreferencesRef.current = JSON.parse(JSON.stringify(preferences))
    }
  }, [preferences, localPreferences])

  const handleSave = () => {
    if (!localPreferences) return
    
    console.log('Saving preferences:', localPreferences)
    
    // savePreferences is a mutation function that doesn't return a promise
    // we need to call it directly without await
    savePreferences(localPreferences, {
      onSuccess: () => {
        originalPreferencesRef.current = JSON.parse(JSON.stringify(localPreferences))
        console.log('Preferences saved successfully')
      },
      onError: (error) => {
        console.error('Failed to save preferences:', error)
      }
    })
  }

  const handleCancel = () => {
    if (originalPreferencesRef.current) {
      setLocalPreferences(JSON.parse(JSON.stringify(originalPreferencesRef.current)))
    }
  }

  const updatePreferences = (updates: Partial<Preferences>) => {
    if (!localPreferences) return
    setLocalPreferences({
      ...localPreferences,
      ...updates
    })
  }

  const sections = [
    { id: 'informations', label: 'Informations', icon: '👤' },
    { id: 'availability', label: 'Disponibilités', icon: '📅' },
    { id: 'expertises', label: 'Types d\'expertises', icon: '📋' },
    { id: 'zones', label: 'Zones', icon: '📍' },
    { id: 'general', label: 'Général', icon: '⚙️' }
  ]

  if (isLoading) {
    return (
      <div className="preferences-loading-state">
        <Loading size="large" text="Chargement des préférences..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="preferences-error-state">
        <p className="preferences-error-message">
          ⚠️ Erreur lors du chargement des préférences
        </p>
        <Button variant="secondary" size="small" onClick={() => window.location.reload()}>
          Réessayer
        </Button>
      </div>
    )
  }

  if (!localPreferences) return null

  return (
    <div className="preferences-page-container">
      <div className="preferences-header">
        <div className="preferences-header-content">
          <div className="preferences-title-row">
            <div>
              <h1 className="preferences-title">Préférences</h1>
              <p className="preferences-subtitle">
                Configurez vos préférences d'expert
              </p>
            </div>
            <div className="preferences-actions">
              {hasChanges() && (
                <Button 
                  variant="secondary" 
                  size="medium" 
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Annuler
                </Button>
              )}
              <Button 
                variant="primary" 
                size="medium" 
                onClick={handleSave}
                loading={isSaving}
                disabled={!hasChanges() || isSaving}
              >
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="preferences-nav-container">
        <nav className="preferences-nav-horizontal">
          {sections.map(section => (
            <button
              key={section.id}
              className={`preferences-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="preferences-nav-icon">{section.icon}</span>
              <span className="preferences-nav-label">{section.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="preferences-content">
        <div className="preferences-content-inner">
          <div className="preferences-main">
            {activeSection === 'informations' && (
              <InformationSection 
                data={localPreferences.informations}
                onChange={(informations) => updatePreferences({ informations })}
              />
            )}
            
            {activeSection === 'availability' && (
              <AvailabilitySection 
                availability={localPreferences.availability}
                domiciles={localPreferences.informations.domicileAddresses}
                onChange={(availability) => updatePreferences({ availability })}
              />
            )}
            
            {activeSection === 'expertises' && (
              <ExpertiseSection 
                expertiseTypes={localPreferences.expertiseTypes}
                onChange={(expertiseTypes) => updatePreferences({ expertiseTypes })}
              />
            )}
            
            {activeSection === 'zones' && (
              <ZonesSection 
                selectedProvinces={localPreferences.selectedProvinces || {}}
                selectedCommunes={localPreferences.selectedCommunes || {}}
                onChange={(provinces, communes) => updatePreferences({
                  selectedProvinces: provinces,
                  selectedCommunes: communes
                })}
              />
            )}
            
            {activeSection === 'general' && (
              <GeneralSection 
                maxExpertises={localPreferences.maxExpertisesPerDay}
                language={localPreferences.preferredLanguage}
                notifications={localPreferences.notificationPreferences}
                onChange={(updates) => updatePreferences(updates)}
              />
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <UnsavedChangesModal 
          onClose={() => setShowModal(false)}
          onSave={() => {
            handleSave()
            setShowModal(false)
          }}
          onDiscard={() => {
            handleCancel()
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}